import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ICianMFUIElement } from '@cian/mf-registry/browser';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import { registerNewbuildingCallbackWidgetManifest } from '../../../services/registerNewbuildingCallbackWidgetManifest';
import {
  INewbuildingCallbackWidgetMicrofrontendAPI,
  IOpenWidgetParams,
  TOpenWidgetResultHandler,
} from '../../../types/microfrontends/newbuildingCallbackWidget';
import { ApplicationContext } from '../../../utils';

export const useNewbuildingCallbackWidget = (resultCallback: TOpenWidgetResultHandler) => {
  const { httpApi, logger, microfrontendsRegistry } = React.useContext(ApplicationContext);

  const widgetRef = useRef<ICianMFUIElement>(null);

  const [widgetManifest, setWidgetManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): INewbuildingCallbackWidgetMicrofrontendAPI => (widgetRef.current?.instance?.customElement as any)?.api,
    [widgetRef],
  );

  const paramsRef = useRef<IOpenWidgetParams>();
  const firstAPICallRef = useRef(false);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (widgetManifest && !firstAPICallRef.current) {
      firstAPICallRef.current = true;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          api['/v1/open'](paramsRef.current as IOpenWidgetParams);
          resultCallback({ isError: false });
        } else {
          resultCallback({ isError: true });
        }
      }, 0);
    }
  }, [resultCallback, getLocalAPI, logger, widgetManifest]);

  const registerAndSetManifest = useCallback(
    async (params: IOpenWidgetParams) => {
      const result = await registerNewbuildingCallbackWidgetManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими параметрами после установки в стейт и инициализации рефа.
        paramsRef.current = params;

        setWidgetManifest(result);
      } else {
        resultCallback({ isError: true });
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openWidget = useCallback(
    async (params: IOpenWidgetParams): Promise<void> => {
      if (widgetManifest) {
        await getLocalAPI()['/v1/open'](params);
        resultCallback({ isError: false });
      } else {
        await registerAndSetManifest(params);
      }
    },
    [getLocalAPI, registerAndSetManifest, resultCallback, widgetManifest],
  );

  return {
    widgetManifest,
    widgetRef,
    openWidget,
  };
};
